import { AddExistingBookingProps, ErrorManagerProps } from '@msc-b2c/interfaces/types';
import styles from './addexistingbooking.module.scss';
import { Button, InputText, Text } from '@msc-b2c/shared-components';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  closeLoader,
  decodeHTMLEntities,
  getActiveSession,
  getKeyboardType,
  handleFormEnterSubmit,
  invalidateAllBookings,
  LinkBookingMyMsc,
  openLoader,
  RootState,
  setErrorManagerCode,
  trackEvent,
  validateFormField,
} from '@msc-b2c/shared-utils';
import { ErrorModal } from '../errormodal/ErrorModal';
import { LinkBookingParams, LinkBookingRequest } from '@msc-b2c/interfaces/interfaces';
import { useDispatch, useSelector } from 'react-redux';
import { triggerClickCtaEvent } from '../utils/myAreaAnalytics';
import { getContainerInfo } from '../utils/analyticsUtils';

const trackAddExistingBookingClickCta = ({
  clickMainName,
  containerElement,
}: {
  clickMainName: string;
  containerElement: HTMLElement | null;
}) => {
  triggerClickCtaEvent({
    cta_dest_link: '',
    click_main_name: clickMainName,
    container_info: getContainerInfo(containerElement),
    element_position: 'Add Existing Booking',
    element_type: 'button',
    click_location: 'fixed',
  });
};

const getTrackedErrorMessage = (
  errorCode: string,
  errorManager: Partial<ErrorManagerProps>
): string => {
  const normalizedErrorCode = errorCode.trim().toLowerCase();
  const errorRule = errorManager?.errorManagementRules?.find(
    (rule) => rule.errorCode?.trim().toLowerCase() === normalizedErrorCode
  );

  return [
    decodeHTMLEntities(errorRule?.errorTitle || errorManager.errorGenericTitle || ''),
    decodeHTMLEntities(errorRule?.errorDescription || errorManager.errorGenericDescription || ''),
  ]
    .filter(Boolean)
    .join(' | ');
};

export function AddExistingBooking({
  addExistingBookingLabel,
  addExistingBookingDescription,
  missingCruiseButtonLabel,
  missingCruiseButtonStyle,
  missingCruiseButtonSize,
  bookingNumberInputPlaceholder,
  validationRulesContainerBookingNumber,
  addBookingButtonLabel,
  addBookingButtonStyle,
  addBookingButtonSize,
  successIcon,
  successModalTitle,
  successModalText,
  continueCtaLabel,
  initialCTAStateForced,
  bookings,
}: Readonly<AddExistingBookingProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [addExistingBooking, setAddExistingBooking] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    clearErrors,
    reset,
    getValues,
  } = useForm({ shouldFocusError: false });

  const dispatch = useDispatch();
  const errorManager = useSelector(
    (state: RootState): Partial<ErrorManagerProps> => ({
      errorGenericTitle: state.errorManager?.errorGenericTitle,
      errorGenericDescription: state.errorManager?.errorGenericDescription,
      errorManagementRules: state.errorManager?.errorManagementRules,
    })
  );

  const { email, bookingNumber } = getActiveSession();
  const bookingNumberKeyboard = getKeyboardType(validationRulesContainerBookingNumber);

  useEffect(() => {
    isLoading ? openLoader('.loader') : closeLoader('.loader');
  }, [isLoading]);

  const handleMissingCruiseClick = () => {
    trackAddExistingBookingClickCta({
      clickMainName: missingCruiseButtonLabel,
      containerElement: containerRef.current,
    });
    setAddExistingBooking(true);
  };

  const handleAddBookingClick = () => {
    trackAddExistingBookingClickCta({
      clickMainName: addBookingButtonLabel,
      containerElement: containerRef.current,
    });
  };

  const handleLinkBooking = async () => {
    const bookingNumber = getValues('booking.number');
    if (!bookingNumber) return;

    setIsLoading(true);

    const requestData: LinkBookingRequest = {
      number: bookingNumber,
    };

    const urlParams: LinkBookingParams = {
      email: email ?? '',
    };

    const linkBookingData = new LinkBookingMyMsc(false, false, requestData, urlParams);

    linkBookingData
      .sendData()
      .then(() => {
        setIsLoading(false);
        reset();
        // Invalidate the cached bookings list so AllMyCruises re-fetches
        // and shows the newly linked booking.
        dispatch(invalidateAllBookings());
        setIsSuccessModalOpen(true);
      })
      .catch((error: any) => {
        setIsLoading(false);
        console.log('Error', error);
        const errorCode = error?.issue_code ?? 'unknown_error';
        trackEvent(
          'error',
          {
            error_source: 'Mulesoft',
            error_message: getTrackedErrorMessage(errorCode, errorManager),
            error_code: errorCode,
          },
          undefined,
          true
        );
        dispatch(setErrorManagerCode(errorCode));
      });
  };

  const onAddBookingSubmit = handleSubmit(async () => {
    handleAddBookingClick();
    await handleLinkBooking();
  }, handleAddBookingClick);

  return (
    <div
      ref={containerRef}
      className={styles['addExistingBookingContainer']}
    >
      <div className={styles['addExistingBookingContainer__description']}>
        <Text
          text={addExistingBookingLabel}
          tagHtml="p"
          styleOption="h3"
        />
        <Text
          text={addExistingBookingDescription}
          tagHtml="p"
          styleOption="cmp-text__big"
        />
      </div>
      {!addExistingBooking &&
        ((!initialCTAStateForced && bookings && bookings.length > 0) || initialCTAStateForced) && (
          <Button
            text={missingCruiseButtonLabel}
            onClick={handleMissingCruiseClick}
            styleOption={`${missingCruiseButtonStyle} ${missingCruiseButtonSize}`}
          />
        )}
      {(addExistingBooking || (!initialCTAStateForced && !bookingNumber)) && (
        <form
          onSubmit={onAddBookingSubmit}
          onKeyDown={handleFormEnterSubmit}
          className={styles['addExistingBookingContainer__addBooking']}
        >
          <InputText
            inputType={bookingNumberKeyboard === 'numeric' ? 'text' : bookingNumberKeyboard}
            inputMode={bookingNumberKeyboard === 'numeric' ? 'numeric' : undefined}
            index={2}
            name={'booking.number'}
            label=""
            placeholder={bookingNumberInputPlaceholder}
            register={register}
            errors={errors}
            clearErrors={clearErrors}
            validation={{
              validate: (value) => {
                return validateFormField(value, validationRulesContainerBookingNumber || []);
              },
              onBlur: () => trigger('booking.number'),
            }}
          />
          <Button
            text={addBookingButtonLabel}
            styleOption={`${addBookingButtonStyle} ${addBookingButtonSize}`}
            buttonType="submit"
          />
        </form>
      )}
      {isSuccessModalOpen && ( //SUCCESS MODAL
        <ErrorModal
          title={successModalTitle}
          message={successModalText}
          iconType={successIcon}
          ctaLabel={continueCtaLabel}
          handleClick={
            !initialCTAStateForced
              ? () => window.location.reload()
              : () => setIsSuccessModalOpen(false)
          }
          closingIcon=""
        />
      )}
    </div>
  );
}

export default AddExistingBooking;
